import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { videos, categoryList } from '@/mocks/videos';
import { formatViews } from '@/utils/format';
import { usePageMeta } from '@/hooks/usePageMeta';

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <i className={`${icon} text-primary-500`} />
      <span className="text-foreground-500">{label}</span>
      <span className="font-medium text-foreground-900">{value}</span>
    </div>
  );
}

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);
  const [toast, setToast] = useState('');

  usePageMeta(
    video ? `${video.title} - 涩涩视频` : '视频详情 - 涩涩视频',
    video ? video.description.slice(0, 60) : '在涩涩视频查看视频详情与相关推荐。',
  );

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const recommendations = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.category === video.category && v.id !== video.id)
      .sort((a, b) => b.views - a.views)
      .slice(0, 6);
  }, [video]);

  if (!video) {
    return (
      <Layout>
        <div className="mx-auto flex min-h-[50vh] w-full flex-col items-center justify-center px-4 py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-film-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">视频不存在或已下线</h1>
          <p className="mt-2 text-sm text-foreground-500">该视频可能已被移除，去看看其他热门内容吧。</p>
          <div className="mt-6 flex gap-3">
            <Link
              to="/"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
            <Link
              to="/search"
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              搜索视频
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const meta = categoryList.find((c) => c.key === video.category);

  const handleShare = () => {
    setToast('分享链接已复制到剪贴板（演示效果）');
  };

  return (
    <Layout>
      <div className="mx-auto w-full px-4 py-6 md:px-8 md:py-10">
        {/* Breadcrumb */}
        <nav className="mb-6 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 md:text-sm" aria-label="面包屑">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/category/${video.category}`} className="cursor-pointer transition-colors hover:text-primary-600">
            {video.categoryName}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="line-clamp-1 font-medium text-foreground-900">{video.title}</span>
        </nav>

        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Left: cover & info */}
          <div className="w-full lg:w-[62%]">
            <div className="relative w-full overflow-hidden rounded-lg bg-background-200">
              <div className="aspect-video w-full">
                <img
                  src={video.cover}
                  alt={`${video.title} 封面大图`}
                  title={`${video.title} - 涩涩视频`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <button
                type="button"
                onClick={() => setToast('本站为内容聚合演示，暂不支持在线播放')}
                className="absolute inset-0 flex cursor-pointer items-center justify-center"
                aria-label="播放（演示）"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/90 text-background-50 transition-transform hover:scale-110 md:h-20 md:w-20">
                  <i className="ri-play-fill ml-1 text-3xl md:text-4xl" />
                </span>
              </button>
              <span className="absolute bottom-3 right-3 rounded-md bg-black/70 px-2.5 py-1 text-xs font-medium text-background-50">
                时长 {video.duration}
              </span>
            </div>

            <h1 className="mt-5 font-heading text-xl font-extrabold leading-snug text-foreground-950 md:text-2xl">
              {video.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-background-200/70 bg-background-50 p-4">
              <InfoRow icon="ri-price-tag-3-line" label="类别" value={`${video.categoryName} · ${video.genre}`} />
              <InfoRow icon="ri-calendar-line" label="上传时间" value={video.uploadDate} />
              <InfoRow icon="ri-time-line" label="时长" value={video.duration} />
              <InfoRow icon="ri-map-pin-line" label="地区" value={`${video.region} · ${video.year}`} />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {video.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
                >
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line text-primary-500" />
                {formatViews(video.views)} 次浏览
              </span>
              <button
                type="button"
                onClick={handleShare}
                className="flex cursor-pointer items-center gap-0 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-line mr-1" />
                分享
              </button>
              <button
                type="button"
                onClick={() => setToast('已收藏（演示效果）')}
                className="flex cursor-pointer items-center gap-0 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-bookmark-line mr-1" />
                收藏
              </button>
            </div>
          </div>

          {/* Right: description + recommendations */}
          <div className="w-full lg:w-[38%]">
            <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-file-text-line text-primary-500" />
                内容简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{video.description}</p>
              <p className="mt-3 rounded-md bg-background-100 p-3 text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-500" />
                本站为视频内容聚合演示平台，视频播放相关功能仅提供封面与资料展示，暂无在线播放接口。
              </p>
            </section>

            <section className="mt-6">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-accent-500" />
                相关推荐
              </h2>
              <div className="flex flex-col gap-3">
                {recommendations.map((v) => (
                  <Link
                    key={v.id}
                    to={`/detail/${v.id}`}
                    className="group flex cursor-pointer gap-3 rounded-lg border border-background-200/70 bg-background-50 p-2 transition-all hover:-translate-y-0.5 hover:border-primary-300"
                  >
                    <div className="relative h-16 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={v.cover}
                        alt={`${v.title} 封面`}
                        title={`${v.title} - 涩涩视频`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-background-50">
                        {v.duration}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                        {v.title}
                      </h3>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs text-foreground-500">
                        <span>{v.genre}</span>
                        <span className="text-background-400">·</span>
                        <span>{formatViews(v.views)}播放</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <section className="mt-6 rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-apps-2-line text-primary-500" />
                相关分类
              </h2>
              <div className="flex flex-wrap gap-2">
                {categoryList.map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      c.key === video.category
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                    }`}
                  >
                    <i className={`${c.icon} text-sm`} />
                    {c.name}
                  </Link>
                ))}
              </div>
              {meta && (
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">{meta.desc}</p>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[80] -translate-x-1/2 animate-fade-up">
          <div className="flex items-center gap-2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50 shadow-sm">
            <i className="ri-checkbox-circle-line text-primary-400" />
            {toast}
          </div>
        </div>
      )}
    </Layout>
  );
}